import React, { useState } from 'react';
import { Cpu, Info, Database } from 'lucide-react';

const familyColors = {
  openai: { bg: 'rgba(16,185,129,0.1)', color: '#34D399', border: 'rgba(16,185,129,0.2)' },
  gemini: { bg: 'rgba(59,130,246,0.1)', color: '#60A5FA', border: 'rgba(59,130,246,0.2)' },
  anthropic: { bg: 'rgba(251,146,60,0.1)', color: '#FB923C', border: 'rgba(251,146,60,0.2)' },
  meta: { bg: 'rgba(168,85,247,0.1)', color: '#A855F7', border: 'rgba(168,85,247,0.2)' },
  mistral: { bg: 'rgba(244,63,94,0.1)', color: '#F43F5E', border: 'rgba(244,63,94,0.2)' },
};
const defaultColor = { bg: 'rgba(148,163,184,0.1)', color: '#94A3B8', border: 'rgba(148,163,184,0.2)' };

const tierColors = {
  economy:  { bg: 'rgba(16,185,129,0.08)',  color: '#34D399', border: 'rgba(16,185,129,0.2)' },
  standard: { bg: 'rgba(99,102,241,0.08)',  color: '#818CF8', border: 'rgba(99,102,241,0.2)' },
  premium:  { bg: 'rgba(168,85,247,0.08)',  color: '#C084FC', border: 'rgba(168,85,247,0.2)' },
};

function formatRate(rate) {
  if (!rate && rate !== 0) return '—';
  if (rate < 0.01) return `$${(rate * 1000000).toFixed(2)}/1M`;
  return `$${rate.toFixed(2)}/1M`;
}

export default function ModelCard({ model, isSelected, onSelect, onShowDetails }) {
  const [hovered, setHovered] = useState(false);
  const fc = familyColors[model.family] || defaultColor;
  const isEmbedding = model.modelType === 'embedding';
  const tierKeys = model.tiers ? Object.keys(model.tiers) : [];

  return (
    <div
      onClick={() => !isEmbedding && onSelect(model.name)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: 12, borderRadius: 10,
        cursor: isEmbedding ? 'default' : 'pointer',
        transition: 'all 0.15s ease',
        border: isSelected && !isEmbedding
          ? '1px solid rgba(124,58,237,0.5)'
          : '1px solid rgba(255,255,255,0.06)',
        background: isSelected && !isEmbedding
          ? 'rgba(124,58,237,0.08)'
          : hovered ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.02)',
        opacity: isEmbedding ? 0.7 : 1,
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {isEmbedding
            ? <Database size={14} color="#64748B" />
            : <Cpu size={14} color="#64748B" />
          }
          <span style={{ fontSize: 13, fontWeight: 500, color: '#F1F5F9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {model.displayName || model.name}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {isEmbedding && (
            <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: 'rgba(234,179,8,0.1)', color: '#FBBF24', border: '1px solid rgba(234,179,8,0.2)' }}>
              embedding
            </span>
          )}
          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: fc.bg, color: fc.color, border: `1px solid ${fc.border}` }}>
            {model.family}
          </span>
        </div>
      </div>

      {/* Default pricing */}
      {model.pricing && !tierKeys.length && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: '#64748B' }}>
            <span style={{ color: '#94A3B8' }}>In:</span> {formatRate(model.pricing.inputTokenRate)}
          </span>
          <span style={{ fontSize: 11, color: '#64748B' }}>
            <span style={{ color: '#94A3B8' }}>Out:</span> {formatRate(model.pricing.outputTokenRate)}
          </span>
        </div>
      )}

      {/* Tier badges */}
      {tierKeys.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
          {tierKeys.map(tier => {
            const tc = tierColors[tier] || defaultColor;
            const tierPricing = model.tiers[tier];
            return (
              <span key={tier} style={{
                fontSize: 10, padding: '2px 7px', borderRadius: 20,
                background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`,
                fontWeight: 500,
              }}>
                {tier} · {formatRate(tierPricing?.inputTokenRate)}
              </span>
            );
          })}
        </div>
      )}

      <button
        onClick={e => { e.stopPropagation(); onShowDetails(model); }}
        style={{
          marginTop: 4, padding: '4px 0', fontSize: 11, color: '#7C3AED',
          background: 'transparent', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        <Info size={11} /> View details
      </button>
    </div>
  );
}
